import { useEffect, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Monitor, RefreshCw, Smartphone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import WindowsInstallSection from "@/components/desktop-app/WindowsInstallSection";
import AndroidInstallSection from "@/components/desktop-app/AndroidInstallSection";
import { useSharedChatWebSocket } from "@/contexts/ChatWebSocketContext";

/**
 * Windows 메신저 설치 안내 모달 — 관리자가 설치를 강제한 사용자가 Windows 브라우저에서 헤더 메신저 아이콘(또는 /chat)을
 * 눌렀을 때 웹 채팅 대신 띄운다 (대표 지시 2026-10-23: 자동 팝업 없음, 강제 설정 시 아이콘 클릭 → 설치 화면,
 * 강제 설정 없으면 웹 메신저 그대로).
 *
 * - 열기는 `openDesktopInstallPrompt()` 로만 한다 (Header.handleChatIconClick). MainLayout 에 한 번 마운트.
 * - 메신저가 로그인하면 서버가 보내는 desktop_presence / online_list.desktop_online 프레임으로 상태를 바로 다시 조회한다
 *   (아이콘 동작이 "앱에서 확인" 으로 바뀐다). "설치 확인" 버튼은 수동 재조회.
 */
const DESKTOP_APP_PATH = "/desktop-app";

export interface DesktopInstallPromptOptions {
  /** android = 휴대폰 브라우저(강제 대상) — APK 설치 카드. 기본 windows(버전 비교 없음, 앱 자동 업데이트) */
  platform?: "windows" | "android";
  /** Android 전용: 설치된 앱이 배포 버전보다 낮음 → "업데이트" 문구 (APK 는 자동 업데이트가 없어 덮어쓰기 설치 안내) */
  update?: { currentVersion: string | null; latestVersion: string | null };
}

type PromptState = (DesktopInstallPromptOptions & { open: true }) | { open: false };
let promptState: PromptState = { open: false };
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
};
const getState = () => promptState;

/** 설치(또는 업데이트) 안내 모달을 지금 띄운다 */
export function openDesktopInstallPrompt(options: DesktopInstallPromptOptions = {}) {
  promptState = { open: true, ...options };
  notify();
}

function closeDesktopInstallPrompt() {
  promptState = { open: false };
  notify();
}

export default function DesktopAppForceDialog() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { subscribe: subscribeWs } = useSharedChatWebSocket();
  const state = useSyncExternalStore(subscribe, getState, getState);
  const open = state.open;
  const isAndroidPrompt = state.open && state.platform === "android";
  const androidUpdate = state.open && state.platform === "android" ? state.update ?? null : null;

  // 메신저가 붙으면(같은 계정) 즉시 상태 재조회 → 아이콘이 "앱에서 확인" 으로 바뀌고 안내를 닫는다
  useEffect(() => {
    return subscribeWs((frame) => {
      const online =
        (frame.type === "desktop_presence" && (frame.payload as { online?: boolean } | undefined)?.online === true) ||
        (frame.type === "online_list" && (frame.payload as { desktop_online?: boolean } | undefined)?.desktop_online === true);
      if (online) {
        queryClient.invalidateQueries({ queryKey: ["desktop-app-status"] });
        if (promptState.open) closeDesktopInstallPrompt();
      }
    });
  }, [subscribeWs, queryClient]);

  const openDetailPage = () => {
    closeDesktopInstallPrompt();
    navigate(DESKTOP_APP_PATH);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && closeDesktopInstallPrompt()}>
      <DialogContent className="dialog-size-auto w-[calc(100%-2rem)] max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isAndroidPrompt ? <Smartphone className="h-5 w-5 text-primary" /> : <Monitor className="h-5 w-5 text-primary" />}
            {isAndroidPrompt
              ? androidUpdate
                ? "메신저 앱을 최신 버전으로 업데이트해 주세요"
                : "HubCore 메신저 앱을 설치해 주세요"
              : "Windows 메신저를 설치해 주세요"}
          </DialogTitle>
          <DialogDescription>
            {isAndroidPrompt ? (
              androidUpdate ? (
                <>
                  설치된 메신저 앱은 <span className="font-medium text-foreground">v{androidUpdate.currentVersion ?? "?"}</span> 이고
                  최신 버전은 <span className="font-medium text-foreground">v{androidUpdate.latestVersion ?? "?"}</span> 입니다.
                  휴대폰 앱은 자동 업데이트가 없으니 아래에서 새 APK 를 받아 설치하세요. 기존 앱 위에 덮어써 업데이트되고 로그인은
                  유지됩니다.
                </>
              ) : (
                <>
                  관리자 설정에 따라 이 휴대폰에서 채팅은 HubCore 메신저 앱으로 이용합니다. 아래에서 APK 를 받아 설치하고 웹과 같은
                  계정으로 로그인하세요. 이미 설치했다면 앱을 열어 로그인하면 됩니다.
                </>
              )
            ) : (
              <>
                관리자 설정에 따라 이 PC에서 채팅은 HubCore 메신저 앱으로 이용합니다. 지금 이 PC에서 메신저가 실행 중이 아닙니다.
                <span className="font-medium text-foreground"> 이미 설치했다면 시작 메뉴에서 HubCore Messenger를 실행</span>하세요.
                아직이라면 아래 설치 파일을 받아 설치하고 웹과 같은 계정으로 로그인하면 됩니다.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isAndroidPrompt ? <AndroidInstallSection compact /> : <WindowsInstallSection compact />}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                queryClient.invalidateQueries({ queryKey: [isAndroidPrompt ? "mobile-app-status" : "desktop-app-status"] })
              }
              title="메신저를 설치하고 로그인했다면 눌러서 다시 확인합니다"
            >
              <RefreshCw className="h-4 w-4 mr-1" /> 설치 확인
            </Button>
            <Button variant="ghost" size="sm" onClick={openDetailPage}>
              <ExternalLink className="h-4 w-4 mr-1" /> 설치 페이지에서 자세히 보기
            </Button>
          </div>
          <Button variant="secondary" onClick={closeDesktopInstallPrompt}>
            닫기
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
